import { createFileRoute } from '@tanstack/react-router'
import { Activity, AlertTriangle, Clock, ShieldCheck, TrendingDown, TrendingUp, Zap } from 'lucide-react'
import { useMemo } from 'react'
import { SignalHistoryTable } from '../components/scanner/SignalHistoryTable'
import { TradeIntelAnalytics } from '../components/TradeIntelAnalytics'
import { LoadingState } from '../components/ui/LoadingState'
import { useAnalysisHistory } from '../hooks/useAnalysisHistory'
import { useDashboardData } from '../hooks/useDashboardData'
import { usePageTitle } from '../hooks/usePageTitle'
import { useRsiSeries } from '../hooks/useRsiSeries'
import { calculateTradeIntelligence } from '../lib/trade-intelligence'
import { useSettingsStore } from '../stores/settingsStore'
import { AnalysisMode } from '../types'

export const Route = createFileRoute('/_auth/scanner')({
    component: ScannerRoute,
})

function ScannerRoute() {
    usePageTitle('Strategy Scanner')
    // FREEZE FIX v3: Granular selectors prevent re-renders from unrelated store changes
    const symbol = useSettingsStore(s => s.symbol)
    const settingsMode = useSettingsStore(s => s.mode)
    const mode = settingsMode === AnalysisMode.VOLUME ? AnalysisMode.VOLUME : AnalysisMode.OI

    // 2. Get Live Data (SAME AS INSPECTOR)
    const { marketData, analysis: liveAnalysis, effectiveExpiry, isLoading: isLiveLoading } = useDashboardData()

    // 3. Get History (Unified Logic)
    const { data: analyzedHistory, isLoading: loadingLocalHistory } = useAnalysisHistory({
        symbol: symbol as any,
        expiry: effectiveExpiry || '',
        range: 20,
        mode,
        baseTimePattern: undefined,
    })

    const history = analyzedHistory || []

    // 4. Normalize Analysis (SAME AS INSPECTOR)
    const normalizedAnalysis = useMemo(() => {
        const base = liveAnalysis
        if (!base) return null
        const raw = base as any
        return {
            ...raw,
            variance: {
                ...(raw.variance || {}),
                totalCount: raw.totalCount ?? raw.variance?.totalCount ?? 0,
                ceCount: raw.ceCount ?? raw.variance?.ceCount ?? 0,
                peCount: raw.peCount ?? raw.variance?.peCount ?? 0,
                finalVariance: raw.variance?.finalVariance ?? raw.variance?.variance ?? raw.oiVariance ?? 0,
            },
        }
    }, [liveAnalysis])

    // 5. Get RSI using S1/R1 strikes from LIVE ANALYSIS (SAME AS INSPECTOR)
    // CRITICAL: Inspector uses analysis.supportStrike/resistanceStrike from LIVE data
    // NOT from normalizedAnalysis (which may have different values)
    const selectedCeStrike = liveAnalysis?.resistanceStrike || 0
    const selectedPeStrike = liveAnalysis?.supportStrike || 0

    const rsiSeriesData = useRsiSeries({
        localHistory: history || [],
        mode: 'Multiple', // SAME AS INSPECTOR's default mode for S1/R1
        selectedSingleStrike: 0,
        selectedCeStrike,
        selectedPeStrike,
        selectedCustomCeStrikes: [],
        selectedCustomPeStrikes: [],
        symbol: symbol,
        expiry: effectiveExpiry,
        rsiTimeFrame: 1, // MUST MATCH INSPECTOR's default (line 182)
    })

    // 6. Extract RSI (EXACT SAME HELPER AS INSPECTOR LINES 590-611)
    const getLatestRsi = (labelSearch: string) => {
        const series = rsiSeriesData.find((s) => s.name.includes(labelSearch))
        if (series && series.data.length > 0) {
            return series.data[series.data.length - 1]!.value
        }
        return 50 // Default Neutral
    }

    const latestCeRsi = selectedCeStrike ? getLatestRsi('CE') : 50
    const latestPeRsi = selectedPeStrike ? getLatestRsi('PE') : 50
    const rsiData = { ceRsi: latestCeRsi, peRsi: latestPeRsi }

    // 7. Calculate Signal for header display
    const signal = useMemo(() => {
        if (!marketData || !normalizedAnalysis || !history || history.length < 3) return null
        return calculateTradeIntelligence({
            marketData,
            analysis: normalizedAnalysis,
            mode,
            history,
            rsiData,
        })
    }, [marketData, normalizedAnalysis, mode, history, rsiData])

    const signalType = signal?.signalType || 'NEUTRAL'
    const setup = signal?.setup
    const isHistoryLoading = loadingLocalHistory || !history || history.length < 3

    return (
        <div className="p-4 sm:p-6 lg:p-8 min-h-screen pb-24 space-y-8 animate-in fade-in duration-500">
            {/* Header Section */}
            <div className="relative overflow-hidden rounded-3xl bg-slate-900/90 backdrop-blur-2xl p-8 text-white shadow-2xl border border-white/10">
                <div className="absolute top-0 right-0 -mt-10 -mr-10 h-64 w-64 rounded-full bg-indigo-500/20 blur-3xl" />
                <div className="absolute bottom-0 left-0 -mb-10 -ml-10 h-64 w-64 rounded-full bg-emerald-500/10 blur-3xl" />

                <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-6">
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-indigo-500/20 rounded-lg backdrop-blur-md border border-indigo-400/30">
                                <Zap className="text-indigo-300" size={24} fill="currentColor" />
                            </div>
                            <h1 className="text-3xl font-black tracking-tight text-white">Strategy Scanner</h1>
                        </div>
                        <p className="text-slate-400 max-w-lg">
                            Real-time algorithmic detection of high-probability setups including Short Covering, Long
                            Unwinding, and Trap Breakouts.
                        </p>
                    </div>

                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-4 bg-white/5 backdrop-blur-md p-4 rounded-2xl border border-white/10">
                            <div className="text-center">
                                <div className="text-2xl font-bold font-mono text-emerald-400">
                                    {signalType === 'BULLISH' ? 1 : 0}
                                </div>
                                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                                    Bullish
                                </div>
                            </div>
                            <div className="w-px h-8 bg-white/10"></div>
                            <div className="text-center">
                                <div className="text-2xl font-bold font-mono text-rose-400">
                                    {signalType === 'BEARISH' ? 1 : 0}
                                </div>
                                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                                    Bearish
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* TradeIntelAnalytics - EXACT SAME PROPS AS INSPECTOR */}
            {marketData && normalizedAnalysis && !isHistoryLoading ? (
                <div className="mb-8">
                    {/* --- SMART NOTIFICATION POPUP (GLOBAL WITHIN SCANNER ONLY) --- */}
                    {liveAnalysis && marketData && (
                        <TradeIntelAnalytics mode={mode} range={20} history={history} rsiData={rsiData} />
                    )}
                </div>
            ) : (
                <LoadingState message="Loading signal intelligence data..." />
            )}

            {/* Signals Grid */}
            {marketData && setup ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {/* Currently we only generate ONE active signal per symbol based on current state */}
                    <StrategyCard
                        signal={{
                            id: 'live-signal',
                            type: signalType,
                            strategyName: setup.header,
                            action: setup.type,
                            timestamp: new Date().toLocaleTimeString(),
                            reason: signal?.thesisPoints.map((t: any) => t.text) || [],
                            color: setup.color,
                        }}
                    />
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center py-20 bg-white/50 dark:bg-slate-900/50 rounded-3xl border border-dashed border-gray-300 dark:border-slate-700">
                    <div className="p-4 bg-gray-50 dark:bg-slate-800 rounded-full mb-4 animate-pulse">
                        <Activity size={32} className="text-gray-400 dark:text-slate-500" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-600 dark:text-slate-300">Scanning Market...</h3>
                    <p className="text-sm text-gray-400 dark:text-slate-500 mt-1 max-w-xs text-center">
                        Analyzing {marketData?.symbol || 'Market'} structure. High probability setups will appear here
                        automatically.
                    </p>
                </div>
            )}

            {/* Signal History Table */}
            <div className="mt-8">
                <SignalHistoryTable
                    currentSignal={signal}
                    spotPrice={marketData?.spotPrice || 0}
                    symbol={marketData?.symbol || 'NIFTY'}
                    initialHistory={history}
                />
            </div>

            {/* Legend Section */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
                <div className="p-5 bg-white/40 dark:bg-slate-900/40 backdrop-blur-xl border border-white/50 dark:border-slate-700/50 shadow-lg ring-1 ring-white/60 dark:ring-slate-700/60 rounded-xl">
                    <div className="flex items-center gap-2 mb-2 font-bold text-emerald-700 dark:text-emerald-400">
                        <TrendingUp size={18} /> Short Covering Rally
                    </div>
                    <p className="text-xs text-gray-500 dark:text-slate-400 leading-relaxed">
                        Occurs when heavy Call Writers start exiting their positions as price breaks Resistance.
                    </p>
                </div>
                <div className="p-5 bg-white/40 dark:bg-slate-900/40 backdrop-blur-xl border border-white/50 dark:border-slate-700/50 shadow-lg ring-1 ring-white/60 dark:ring-slate-700/60 rounded-xl">
                    <div className="flex items-center gap-2 mb-2 font-bold text-rose-700 dark:text-rose-400">
                        <TrendingDown size={18} /> Long Unwinding Fall
                    </div>
                    <p className="text-xs text-gray-500 dark:text-slate-400 leading-relaxed">
                        Occurs when heavy Put Writers panic and exit as price breaks Support.
                    </p>
                </div>
                <div className="p-5 bg-white/40 dark:bg-slate-900/40 backdrop-blur-xl border border-white/50 dark:border-slate-700/50 shadow-lg ring-1 ring-white/60 dark:ring-slate-700/60 rounded-xl">
                    <div className="flex items-center gap-2 mb-2 font-bold text-indigo-700 dark:text-indigo-400">
                        <AlertTriangle size={18} /> Reversal Trap
                    </div>
                    <p className="text-xs text-gray-500 dark:text-slate-400 leading-relaxed">
                        When price moves against the trend but OI suggests 'Big Hands' are accumulating.
                    </p>
                </div>
            </div>
        </div>
    )
}

// Inline Strategy Card Component
const StrategyCard: React.FC<{ signal: any }> = ({ signal }) => {
    const isBullish = signal.type === 'BULLISH'
    const isBearish = signal.type === 'BEARISH'
    const colorClass = isBullish
        ? 'from-emerald-500 to-teal-500'
        : isBearish
          ? 'from-rose-500 to-red-500'
          : 'from-slate-500 to-gray-500'
    const ringClass = isBullish
        ? 'ring-emerald-500/30 border-emerald-500/20'
        : isBearish
          ? 'ring-rose-500/30 border-rose-500/20'
          : 'ring-slate-400/30 border-slate-400/20'

    // Fix: Handle Neutral State Color
    const textClass = isBullish
        ? 'text-emerald-700 dark:text-emerald-400'
        : isBearish
          ? 'text-rose-700 dark:text-rose-400'
          : 'text-slate-600 dark:text-slate-300'

    return (
        <div
            className={`relative overflow-hidden rounded-[20px] border bg-white/60 dark:bg-slate-900/60 backdrop-blur-2xl shadow-lg ring-1 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-2xl cursor-pointer ${ringClass}`}
        >
            <div className={`absolute top-0 left-0 w-1.5 h-full bg-linear-to-b ${colorClass}`} />
            <div className="p-5 pl-7 relative z-10">
                <div className="flex justify-between items-start mb-4">
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <span
                                className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest text-white shadow-md bg-linear-to-r ${colorClass}`}
                            >
                                {signal.type}
                            </span>
                            <span className="text-[10px] text-slate-400 dark:text-slate-500 font-bold uppercase tracking-wider flex items-center gap-1 bg-white/50 dark:bg-slate-800/50 px-2 py-0.5 rounded-md border border-white/40 dark:border-slate-700/40">
                                <Clock size={10} /> {signal.timestamp}
                            </span>
                        </div>
                        <h3 className="text-lg font-black text-slate-800 dark:text-slate-100 leading-tight tracking-tight">
                            {signal.strategyName}
                        </h3>
                    </div>
                    <div className="flex flex-col items-end">
                        <div className={`text-2xl font-black tracking-tighter ${textClass}`}>{signal.action}</div>
                    </div>
                </div>
                <div className="space-y-2 mb-5">
                    {signal.reason.map((reason: string, idx: number) => (
                        <div
                            key={idx}
                            className="flex items-start gap-2 text-xs font-medium text-slate-600 dark:text-slate-400 bg-white/40 dark:bg-slate-800/40 p-2 rounded-lg border border-white/40 dark:border-slate-700/40"
                        >
                            <ShieldCheck size={14} className={`mt-0.5 shrink-0 ${textClass}`} />
                            <span>{reason}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}
